import { Vector3 as BABYLON_Vector3, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
// Side effect
import './shaders/accumulation_buffer.fragment.js';
import './shaders/ao_half_depth.fragment.js';
import './shaders/ao_compute.fragment.js';
import './shaders/ao_temporal_filter.fragment.js';
import './shaders/ao_half_normals.fragment.js';
import './shaders/ao_apply.fragment.js';
import { blue_noise_64x64x1 } from './bin/BlueNoiseTexture64x64.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { clamp, matrix_pool, radians } from '../Math.js';
import { RawTexture as BABYLON_RawTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/rawTexture.js';
import { BilateralBlur } from './BilateralBlur.js';
const DEPTH_SAMPLER = 'depthSampler';
const NOISE_SAMPLER = 'noiseSampler';
const SAMPLE_KERNEL = 'sampleKernel[0]';
const NOISE_SCALE = 'noiseScale';
const RADIUS = 'radius';
const INVERTED_PROJECTION_MATRIX = 'invertedProjectionMatrix';
const PROJECTION_MATRIX = 'projectionMatrix';
const NOISE_UV_OFFSET = 'noiseUvOffset';
const INVERSE_DEPTH_DIMENSIONS = 'inverseDepthDimensions';
const PROJECTION_MATRIX_Z_VALUES = 'projectionMatrixZ';
const PREVIOUS_PROJECTION_MATRIX_Z_VALUES = 'previousProjectionMatrixZ';
const PREVIOUS_AO_SAMPLER = 'previousAoSampler';
const PREVIOUS_DEPTH_SAMPLER = 'previousDepthSampler';
const AO_SAMPLER = 'aoSampler';
const CURRENT_VIEWSPACE_TO_PREVIOUS_PROJECTION_SPACE_MATRIX = 'viewToPreviousProjMatrix';
const PREVIOUS_PROJECTED_TO_CURRENT_VIEW = 'previousProjectedToCurrentView';
const INVERSE_VIEW_MATRIX = 'inverseViewMatrix';
// For AO apply
const HALF_LINEAR_DEPTH_SAMPLER = 'halfLinearDepthSampler';
const INVERSE_PROJECTION_MATRIX = 'invertedProjectionMatrix';
const INVERSE_HALF_RESOLUTION = 'inverseHalfResolution';
const AO_STRENGTH = 'aoStrength';
export class AmbientOcclusion {
    constructor(scene, width, height, post_process_camera, camera, depth, samples, enable_normal_filtering, gpu_profiler) {
        this._ao_kernel_sample_cycle = 0;
        this._apply_ao_target = null;
        // This is just a helper variable to tell us if we really need to recompile the compute_ao shader when updating sample count.
        this._last_compiled_number_of_samples = -1;
        this._noise_offset_index = 0;
        this.ao_radius = 0.5;
        this.ao_strength = 1.0;
        // This can be used to generate more "frames" of AO samples.
        // But through testing, using the "moving" noise texture yielded better results.
        this._ao_kernel_frames = 1;
        this._profiling = null;
        this._scene = scene;
        this._camera = camera;
        this._depth = depth;
        this._post_process_camera = post_process_camera;
        this._enable_normal_filtering = enable_normal_filtering;
        if (gpu_profiler !== null) {
            this._profiling = {
                profiler: gpu_profiler,
                section_total: gpu_profiler.create_section('AO Total'),
                section_downscale_depth_normals: gpu_profiler.create_section('AO Downscale Depth/Normals'),
                section_compute: gpu_profiler.create_section('AO Compute'),
                section_bilateral_filter_0: gpu_profiler.create_section('AO Bilateral 0'),
                section_bilateral_filter_1: gpu_profiler.create_section('AO Bilateral 1'),
                section_temporal_filter: gpu_profiler.create_section('AO Temporal filter')
            };
        }
        const noise_offset_count = 16;
        this._noise_offsets = [];
        for (let i = 0; i < noise_offset_count; i++) {
            this._noise_offsets.push({
                x: Math.random(), y: Math.random()
            });
        }
        this._previous_projection_matrix = new BABYLON_Matrix();
        this._previous_view_matrix = new BABYLON_Matrix();
        this._ao_kernel = [];
        this._noise_size = 64;
        const noise_texture_data = new Uint8Array(this._noise_size * this._noise_size * 3);
        // Read blue noise data as angles, and calculate the vec2's from it for the shader to use.
        for (let i = 0; i < this._noise_size * this._noise_size; i++) {
            const angle = 2.0 * Math.PI * blue_noise_64x64x1[i] / 255.0;
            noise_texture_data[3 * i + 0] = clamp((Math.cos(angle) * 0.5 + 0.5) * 255.0, 0, 255);
            noise_texture_data[3 * i + 1] = clamp((Math.sin(angle) * 0.5 + 0.5) * 255.0, 0, 255);
            noise_texture_data[3 * i + 2] = clamp((Math.random() * 0.5 + 0.5) * 255.0, 0, 255);
        }
        this._noise_texture = new BABYLON_RawTexture(noise_texture_data, this._noise_size, this._noise_size, BABYLON_Engine.TEXTUREFORMAT_RGB, scene, false, false, BABYLON_RawTexture.NEAREST_SAMPLINGMODE, BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE, BABYLON_Constants.TEXTURE_CREATIONFLAG_STORAGE, false);
        this._noise_texture.wrapU = BABYLON_Texture.WRAP_ADDRESSMODE;
        this._noise_texture.wrapV = BABYLON_Texture.WRAP_ADDRESSMODE;
        const downsampled_width = width / 2;
        const downsampled_height = height / 2;
        this._downsampled_depth = this.create_depth_buffer('downsampled_depth_0', downsampled_width, downsampled_height);
        this._previous_downsampled_depth = this.create_depth_buffer('downsampled_depth_1', downsampled_width, downsampled_height);
        this._bilateral_filter = this.create_bilateral_filter();
        this._ao_buffer_write = this.create_ao_buffer('ao_write', downsampled_width, downsampled_height);
        this._ao_buffer_accumulate = this.create_ao_buffer('ao_accumulate', downsampled_width, downsampled_height);
        this._ao_buffer_intermediate = this.create_ao_buffer('ao_intermediate', downsampled_width, downsampled_height);
        this._compute_ao = this.create_ao_compute_effect(samples);
        this._temporal_filter = new BABYLON_PostProcess('temporal_filter', 'ao_temporal_filter', [
            INVERTED_PROJECTION_MATRIX,
            CURRENT_VIEWSPACE_TO_PREVIOUS_PROJECTION_SPACE_MATRIX,
            PREVIOUS_PROJECTED_TO_CURRENT_VIEW,
            INVERSE_VIEW_MATRIX,
            PROJECTION_MATRIX_Z_VALUES,
            PREVIOUS_PROJECTION_MATRIX_Z_VALUES
        ], [
            PREVIOUS_AO_SAMPLER, PREVIOUS_DEPTH_SAMPLER,
            AO_SAMPLER, DEPTH_SAMPLER
        ], { width: 4, height: 4 }, post_process_camera);
        this._temporal_filter.forceFullscreenViewport = true;
        this._temporal_filter.onApply = (effect) => {
            this.bind_temporal_filter(effect);
        };
        this._downsample_depth = new BABYLON_PostProcess('downsample_depth', 'ao_half_depth', [
            INVERTED_PROJECTION_MATRIX
        ], [
            DEPTH_SAMPLER
        ], { width: 4, height: 4 }, post_process_camera);
        this._downsample_depth.forceFullscreenViewport = true;
        this._downsample_depth.onApply = (effect) => {
            effect.setTexture(DEPTH_SAMPLER, this._depth);
            const inverse_projection_matrix = this._camera.getProjectionMatrix().invertToRef(matrix_pool.fetch());
            effect.setMatrix(INVERTED_PROJECTION_MATRIX, inverse_projection_matrix);
            matrix_pool.return(inverse_projection_matrix);
        };
        this._downsample_normals = null;
        this._downsampled_normals = null;
        if (this.enable_normal_filtering) {
            this.initialize_normal_effect_and_render_target(downsampled_width, downsampled_height);
        }
        this._apply_ao = new BABYLON_PostProcess('ao_apply', 'ao_apply', [
            AO_STRENGTH, INVERSE_HALF_RESOLUTION, INVERSE_PROJECTION_MATRIX
        ], [
            DEPTH_SAMPLER, AO_SAMPLER, HALF_LINEAR_DEPTH_SAMPLER
        ], { width: 4, height: 4 }, post_process_camera);
        this._apply_ao.onApply = (effect) => {
            effect.setTexture(DEPTH_SAMPLER, this._apply_ao_target.get_depth_texture());
            effect.setTexture(AO_SAMPLER, this.get_ao_sampler());
            const half_depth_sampler = this.get_downsampled_linear_depth_sampler();
            effect.setTexture(HALF_LINEAR_DEPTH_SAMPLER, half_depth_sampler);
            effect.setFloat(AO_STRENGTH, this.ao_strength);
            effect.setFloat2(INVERSE_HALF_RESOLUTION, 1.0 / half_depth_sampler.getRenderWidth(), 1.0 / half_depth_sampler.getRenderHeight());
            const inverse_projection_matrix = this._camera.getProjectionMatrix().invertToRef(matrix_pool.fetch());
            effect.setMatrix(INVERSE_PROJECTION_MATRIX, inverse_projection_matrix);
            matrix_pool.return(inverse_projection_matrix);
        };
        this._apply_ao.alphaMode = BABYLON_Engine.ALPHA_MULTIPLY;
    }
    get enable_normal_filtering() {
        return this._enable_normal_filtering;
    }
    set enable_normal_filtering(value) {
        if (value === this._enable_normal_filtering)
            return;
        this._enable_normal_filtering = value;
        // Recreate bilateral filter to account for or ignore normals
        this._bilateral_filter.dispose();
        this._bilateral_filter = this.create_bilateral_filter();
        if (this._enable_normal_filtering) {
            this.initialize_normal_effect_and_render_target(this._downsampled_depth.getRenderWidth(), this._downsampled_depth.getRenderHeight());
        }
        else {
            this._downsample_normals.dispose();
            this._downsampled_normals.dispose();
            this._downsample_normals = null;
            this._downsampled_normals = null;
        }
        // Compute AO uses normal buffer, so it needs recompile
        this._compute_ao.dispose();
        this.create_ao_compute_effect(this._last_compiled_number_of_samples);
    }
    create_bilateral_filter() {
        const bilateral_filter = new BilateralBlur(2, this._scene, this._enable_normal_filtering);
        bilateral_filter.depth_threshold = 0.04;
        bilateral_filter.normal_angle_threshold = radians(30);
        return bilateral_filter;
    }
    initialize_normal_effect_and_render_target(width, height) {
        this._downsampled_normals = new BABYLON_RenderTargetTexture('downsampled_normals', { width: width, height: height }, this._scene, undefined, undefined, BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE, false, BABYLON_Constants.TEXTURE_NEAREST_SAMPLINGMODE, false, false, false, BABYLON_Constants.TEXTUREFORMAT_RG, false);
        this._downsampled_normals.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        this._downsampled_normals.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        this._downsample_normals = new BABYLON_PostProcess('ao_half_normals', 'ao_half_normals', [INVERTED_PROJECTION_MATRIX, INVERSE_DEPTH_DIMENSIONS], [DEPTH_SAMPLER], { width: 4, height: 4 }, this._post_process_camera);
        this._downsample_normals.onApply = (effect) => {
            effect.setFloat2(INVERSE_DEPTH_DIMENSIONS, 1.0 / this._depth.getSize().width, 1.0 / this._depth.getSize().height);
            effect.setTexture(DEPTH_SAMPLER, this._depth);
            const inverse_projection_matrix = this._camera.getProjectionMatrix().invertToRef(matrix_pool.fetch());
            effect.setMatrix(INVERTED_PROJECTION_MATRIX, inverse_projection_matrix);
            matrix_pool.return(inverse_projection_matrix);
        };
    }
    set_ao_sample_count(samples) {
        if (this._last_compiled_number_of_samples === samples) {
            // Early out, no change.
            return;
        }
        this._compute_ao.dispose();
        this.create_ao_compute_effect(samples);
    }
    create_ao_compute_effect(samples) {
        const defines = [];
        defines.push('#define sampleKernelSize ' + samples);
        this._compute_ao = new BABYLON_PostProcess('compute_ao', 'ao_compute', [
            INVERTED_PROJECTION_MATRIX,
            PROJECTION_MATRIX,
            RADIUS,
            SAMPLE_KERNEL,
            NOISE_SCALE,
            NOISE_UV_OFFSET,
            INVERSE_DEPTH_DIMENSIONS,
            PROJECTION_MATRIX_Z_VALUES
        ], [
            DEPTH_SAMPLER,
            NOISE_SAMPLER
        ], { width: 4, height: 4 }, this._post_process_camera, undefined, undefined, false, defines.join('\n'));
        this._compute_ao.forceFullscreenViewport = true;
        this._compute_ao.onApply = (effect) => {
            this.bind_compute_ao(effect);
        };
        // We also create the kernel here, as number of samples might have been updated.
        this.create_kernel(samples, this._ao_kernel_frames);
        // Store how many samples we compiled with
        this._last_compiled_number_of_samples = samples;
        return this._compute_ao;
    }
    bind_temporal_filter(effect) {
        const projection_matrix = this._camera.getProjectionMatrix();
        const view_matrix = this._camera.getViewMatrix();
        const inverse_projection_matrix = projection_matrix.invertToRef(matrix_pool.fetch());
        const inverse_view_matrix = view_matrix.invertToRef(matrix_pool.fetch());
        const previous_view_projection = this._previous_view_matrix.multiplyToRef(this._previous_projection_matrix, matrix_pool.fetch());
        const view_to_previous_projection_matrix = inverse_view_matrix.multiplyToRef(previous_view_projection, matrix_pool.fetch());
        const previous_projected_to_current_view = view_to_previous_projection_matrix.invertToRef(matrix_pool.fetch());
        effect.setMatrix(INVERTED_PROJECTION_MATRIX, inverse_projection_matrix);
        effect.setMatrix(CURRENT_VIEWSPACE_TO_PREVIOUS_PROJECTION_SPACE_MATRIX, view_to_previous_projection_matrix);
        effect.setMatrix(PREVIOUS_PROJECTED_TO_CURRENT_VIEW, previous_projected_to_current_view);
        effect.setMatrix(INVERSE_VIEW_MATRIX, inverse_view_matrix);
        effect.setFloat2(PROJECTION_MATRIX_Z_VALUES, projection_matrix.m[10], projection_matrix.m[14]);
        effect.setFloat2(PREVIOUS_PROJECTION_MATRIX_Z_VALUES, this._previous_projection_matrix.m[10], this._previous_projection_matrix.m[14]);
        this._previous_projection_matrix.copyFrom(projection_matrix);
        this._previous_view_matrix.copyFrom(view_matrix);
        matrix_pool.return(inverse_projection_matrix, view_to_previous_projection_matrix, previous_view_projection, inverse_view_matrix, previous_projected_to_current_view);
        effect.setTexture(DEPTH_SAMPLER, this._downsampled_depth);
        effect.setTexture(PREVIOUS_DEPTH_SAMPLER, this._previous_downsampled_depth);
        effect.setTexture(AO_SAMPLER, this._ao_buffer_write);
        effect.setTexture(PREVIOUS_AO_SAMPLER, this._ao_buffer_accumulate);
    }
    bind_compute_ao(effect) {
        const projection_matrix = this._camera.getProjectionMatrix();
        const inverse_projection_matrix = projection_matrix.invertToRef(matrix_pool.fetch());
        effect.setFloat2(INVERSE_DEPTH_DIMENSIONS, 1.0 / this._downsampled_depth.getRenderWidth(), 1.0 / this._downsampled_depth.getRenderHeight());
        effect.setMatrix(INVERTED_PROJECTION_MATRIX, inverse_projection_matrix);
        effect.setMatrix(PROJECTION_MATRIX, projection_matrix);
        effect.setTexture(DEPTH_SAMPLER, this._downsampled_depth);
        effect.setTexture(NOISE_SAMPLER, this._noise_texture);
        effect.setFloat2(NOISE_SCALE, this._ao_buffer_write.getRenderWidth() / this._noise_size, this._ao_buffer_write.getRenderHeight() / this._noise_size);
        // effect.setFloat2(NOISE_UV_OFFSET, this.noise_offsets[this.noise_offset_index].x, this.noise_offsets[this.noise_offset_index].y)
        effect.setFloat2(NOISE_UV_OFFSET, Math.random(), Math.random());
        this._noise_offset_index = (this._noise_offset_index + 1) % this._noise_offsets.length;
        effect.setFloatArray3(SAMPLE_KERNEL, this._ao_kernel[this._ao_kernel_sample_cycle]);
        this._ao_kernel_sample_cycle = (this._ao_kernel_sample_cycle + 1) % this._ao_kernel.length;
        effect.setFloat(RADIUS, this.ao_radius);
        effect.setFloat2(PROJECTION_MATRIX_Z_VALUES, projection_matrix.m[10], projection_matrix.m[14]);
        matrix_pool.return(inverse_projection_matrix);
    }
    render(scene) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        (_a = this._profiling) === null || _a === void 0 ? void 0 : _a.section_total.start();
        (_b = this._profiling) === null || _b === void 0 ? void 0 : _b.section_downscale_depth_normals.start();
        // Half resolution depth and linearize
        scene.postProcessManager.directRender([this._downsample_depth], this._downsampled_depth.renderTarget, true);
        if (this.enable_normal_filtering) {
            // Compute normals from full res depth
            scene.postProcessManager.directRender([this._downsample_normals], this._downsampled_normals.renderTarget, true);
        }
        (_c = this._profiling) === null || _c === void 0 ? void 0 : _c.section_downscale_depth_normals.stop();
        // Set to true for AO debugging. Disables the bilateral filter and the temporal reprojection.
        // Directly renders the computed AO from this frame. Note that it renders to a different target compared to normal AO compute
        const debug_skip_filters = false;
        if (debug_skip_filters) {
            (_d = this._profiling) === null || _d === void 0 ? void 0 : _d.section_compute.start();
            scene.postProcessManager.directRender([this._compute_ao], this._ao_buffer_accumulate.renderTarget, true);
            (_e = this._profiling) === null || _e === void 0 ? void 0 : _e.section_compute.stop();
            (_f = this._profiling) === null || _f === void 0 ? void 0 : _f.section_total.stop();
            return;
        }
        // Normal AO compute
        (_g = this._profiling) === null || _g === void 0 ? void 0 : _g.section_compute.start();
        scene.postProcessManager.directRender([this._compute_ao], this._ao_buffer_write.renderTarget, true);
        (_h = this._profiling) === null || _h === void 0 ? void 0 : _h.section_compute.stop();
        // Add bilateral blur x 2 (5x5) into ao_buffer_write (itself) (ao_buffer_write => intermediate => ao_buffer_write)
        (_j = this._profiling) === null || _j === void 0 ? void 0 : _j.section_bilateral_filter_0.start();
        this._bilateral_filter.render(scene, this._ao_buffer_write, this._downsampled_depth, this._downsampled_normals, this._ao_buffer_intermediate, this._ao_buffer_write);
        (_k = this._profiling) === null || _k === void 0 ? void 0 : _k.section_bilateral_filter_0.stop();
        // Add temporal filter (ao_buffer_write + ao_buffer_accum => intermediate)
        (_l = this._profiling) === null || _l === void 0 ? void 0 : _l.section_temporal_filter.start();
        scene.postProcessManager.directRender([this._temporal_filter], this._ao_buffer_intermediate.renderTarget, true);
        (_m = this._profiling) === null || _m === void 0 ? void 0 : _m.section_temporal_filter.stop();
        // Bilateral blur again (intermediate => ao_buffer_write => ao_buffer_accum)
        (_o = this._profiling) === null || _o === void 0 ? void 0 : _o.section_bilateral_filter_1.start();
        this._bilateral_filter.render(scene, this._ao_buffer_intermediate, this._downsampled_depth, this._downsampled_normals, this._ao_buffer_write, this._ao_buffer_accumulate);
        (_p = this._profiling) === null || _p === void 0 ? void 0 : _p.section_bilateral_filter_1.stop();
        (_q = this._profiling) === null || _q === void 0 ? void 0 : _q.section_total.stop();
    }
    apply(framebuffer) {
        this._apply_ao_target = framebuffer;
        const gl = this._scene.getEngine()._gl;
        gl.colorMask(true, true, true, false);
        this._scene.postProcessManager.directRender([this._apply_ao], framebuffer.get_render_target().renderTarget, true);
        gl.colorMask(true, true, true, true);
        this._scene.getEngine().setColorWrite(true);
        // For some reason, unless we change our alpha blend state, Babylon fails to apply other alpha blend state for materials later.
        // It seems that if it's set to anything other than DISABLE, Babylon doesn't apply blend state for the meshes/material's requested blend mode.
        this._scene.getEngine().setAlphaMode(BABYLON_Engine.ALPHA_DISABLE);
    }
    create_ao_buffer(name, width, height) {
        const texture_component_type = BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE;
        const buffer = new BABYLON_RenderTargetTexture(name, { width: width, height: height }, this._scene, undefined, undefined, texture_component_type, false, BABYLON_Texture.BILINEAR_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RED);
        buffer.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        buffer.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        buffer.ignoreCameraViewport = true;
        return buffer;
    }
    create_depth_buffer(name, width, height) {
        const depth_component_type = BABYLON_Engine.TEXTURETYPE_FLOAT;
        const buffer = new BABYLON_RenderTargetTexture(name, { width: width, height: height }, this._scene, false, undefined, depth_component_type, false, BABYLON_Texture.NEAREST_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RED);
        buffer.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        buffer.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        buffer.ignoreCameraViewport = true;
        return buffer;
    }
    get_ao_sampler() {
        return this._ao_buffer_accumulate;
    }
    get_downsampled_linear_depth_sampler() {
        return this._downsampled_depth;
    }
    flip_flop() {
        const swap = this._downsampled_depth;
        this._downsampled_depth = this._previous_downsampled_depth;
        this._previous_downsampled_depth = swap;
    }
    resize(width, height) {
        var _a;
        const downsampled_width = width / 2;
        const downsampled_height = height / 2;
        const downsampled_size = { width: downsampled_width, height: downsampled_height };
        this._ao_buffer_write.resize(downsampled_size);
        this._ao_buffer_accumulate.resize(downsampled_size);
        this._ao_buffer_intermediate.resize(downsampled_size);
        this._downsampled_depth.resize(downsampled_size);
        this._previous_downsampled_depth.resize(downsampled_size);
        (_a = this._downsampled_normals) === null || _a === void 0 ? void 0 : _a.resize(downsampled_size);
    }
    dispose() {
        var _a, _b;
        // Dispose texture
        this._noise_texture.dispose();
        // Dispose render targets
        this._ao_buffer_write.dispose();
        this._ao_buffer_accumulate.dispose();
        this._ao_buffer_intermediate.dispose();
        this._downsampled_depth.dispose();
        this._previous_downsampled_depth.dispose();
        (_a = this._downsampled_normals) === null || _a === void 0 ? void 0 : _a.dispose();
        // Dispose effects
        this._bilateral_filter.dispose();
        this._compute_ao.dispose();
        this._temporal_filter.dispose();
        this._downsample_depth.dispose();
        (_b = this._downsample_normals) === null || _b === void 0 ? void 0 : _b.dispose();
        this._apply_ao.dispose();
        this._ao_kernel = [];
    }
    create_kernel(sample_count, frame_count) {
        this._ao_kernel = new Array(frame_count);
        const golden_ratio = (1.0 + Math.sqrt(5.0)) / 2.0;
        const total_sample_count = frame_count * sample_count;
        const cached_vector = new BABYLON_Vector3();
        for (let frame = 0; frame < frame_count; frame++) {
            this._ao_kernel[frame] = new Float32Array(3 * sample_count);
            const kernel = this._ao_kernel[frame];
            for (let i = 0; i < sample_count; i++) {
                // Generate a sampling vector using the "Fibonacci Lattice"
                const sample_index = i * frame_count + frame;
                const theta = 2.0 * Math.PI * sample_index / golden_ratio;
                const phi = Math.acos(1.0 - 2.0 * (sample_index + 0.5) / total_sample_count);
                cached_vector.x = Math.sin(phi) * Math.cos(theta);
                cached_vector.y = Math.sin(phi) * Math.sin(theta);
                cached_vector.z = Math.abs(Math.cos(phi));
                const scale = (1.0 + sample_index) / total_sample_count;
                cached_vector.scaleInPlace(scale * scale);
                kernel[3 * i + 0] = cached_vector.x;
                kernel[3 * i + 1] = cached_vector.y;
                kernel[3 * i + 2] = cached_vector.z;
            }
        }
    }
}
//# sourceMappingURL=AmbientOcclusion.js.map